import React, { ComponentProps, useCallback } from 'react';
import PropTypes, { InferProps } from 'prop-types';
import mailcheck from 'mailcheck';
import { Controller } from 'react-hook-form';
import TextField, { Variant } from './TextField';

const emailFieldPropertyTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  helperText: PropTypes.node,
  formatSuggestion: PropTypes.func,
  useStringOnClick: PropTypes.bool,
};

type EmailFieldProperties = Omit<
  InferProps<typeof emailFieldPropertyTypes>,
  'onChange' | 'formatSuggestion' | 'helperText'
> & {
  onChange?: (
    value: string | { target: { value: string } },
  ) => void;
  formatSuggestion?: (email: string) => React.ReactNode;
  helperText?: React.ReactNode;
  name: string;
  id: string;
  variant?: Variant;
  onBlur?: () => void;
  fullWidth?: boolean;
  required?: boolean;
  autoComplete?: string;
  type?: string;
};

const EmailField = React.forwardRef(
  (
    {
      value,
      onChange,
      helperText: originalHelperText,
      formatSuggestion,
      useStringOnClick,
      ...properties
    }: EmailFieldProperties,
    reference: React.ForwardedRef<HTMLDivElement>,
  ) => {
    const emailValue = value ?? '';
    const { full: suggestedEmail } = {
      ...mailcheck.run({ email: emailValue }),
    };

    const formatSuggestionValue = formatSuggestion
      ?? ((email: string) => `Did you mean ${email}?`);

    const handleSuggestionSelect = () => {
      const email = suggestedEmail as string;
      if (useStringOnClick) {
        onChange?.(email);
        return;
      }
      onChange?.({ target: { value: email } });
    };

    const emailSuggestion = (
      suggestedEmail && suggestedEmail !== emailValue
    ) && (
      <span
        onClick={handleSuggestionSelect}
        onKeyDown={handleSuggestionSelect}
        role="button"
        tabIndex={-1}
      >
        {formatSuggestionValue(suggestedEmail)}
      </span>
    );
    const helperText = emailSuggestion
      || originalHelperText;

    return (
      <TextField
        {...properties}
        type="text"
        onChange={onChange}
        value={emailValue}
        helperText={helperText}
        ref={reference}
      />
    );
  },
);

EmailField.propTypes = emailFieldPropertyTypes;

const emailFieldWithControllerPropertyTypes = {
  rules: PropTypes.shape({}),
  name: PropTypes.string.isRequired,
  control: PropTypes.shape({}).isRequired,
  defaultValue: PropTypes.string,
};

type EmailFieldWithControllerProperties = Omit<
  InferProps<
    typeof emailFieldWithControllerPropertyTypes
  >,
  'control'
> & {
  control: ComponentProps<typeof Controller>['control'];
  rules?: ComponentProps<typeof Controller>['rules'];
  formatSuggestion?: (email: string) => React.ReactNode;
  useStringOnClick?: boolean;
  variant?: Variant;
  id?: string;
  label?: string;
  fullWidth?: boolean;
  required?: boolean;
  autoComplete?: string;
  type?: string;
};

export const EmailFieldWithController = ({
  rules,
  name,
  control,
  defaultValue,
  id,
  ...properties
}: EmailFieldWithControllerProperties) => {
  const fieldId = id ?? name;
  const render = useCallback(
    ({
      onChange, onBlur, value, ref,
    }: {
      onChange: (value: string) => void;
      onBlur: () => void;
      value: string;
      ref: React.RefObject<HTMLInputElement>;
    }) => (
      <EmailField
        onChange={
          onChange as
            EmailFieldProperties['onChange']
        }
        onBlur={onBlur}
        value={value}
        ref={ref}
        name={name}
        id={fieldId}
        useStringOnClick
        {...properties}
      />
    ),
    [name, fieldId, properties],
  );
  return (
    <Controller
      control={control}
      name={name}
      rules={rules}
      defaultValue={defaultValue ?? ''}
      render={render}
    />
  );
};

EmailFieldWithController.propTypes = emailFieldWithControllerPropertyTypes;

export default EmailField;
